import * as moment from 'moment';
import * as _ from "lodash";
import { useMemo, useState } from 'react';
import { IDonorRegistration, IDonorStatus } from '@nkr/models/index';
import { BodyScrollEvent, CellClickedEvent, ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { VoucherEducationChoice } from "@nkr/enums/voucher-education-choice";
import { AgGridReact } from 'ag-grid-react';
import { ButtonToolbar, FormControl, Glyphicon, Label, OverlayTrigger, Popover } from 'react-bootstrap';
import { GridWrapper } from './styles';
import { Utility } from '@nkr/services/utility';
import { DonorIntakeAction } from '../donor-intake-action';
import { ICoordinator } from '../../coordinator';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import ActionsDropdown from '../actions-dropdown';
import ClosedLoopLinkedDonorTypeRenderer from './closed-loop-linked-donor-type-renderer';
import { isAdvReimburseEnabledForCenter } from '../../../watr/components/watr-nav-area';
import { hasForeignResidence } from '../../../watr/components/utils/international-eligibility';

const popoverHoverFocus = (msg: string) => (
    <Popover id='popover-trigger-hover-focus'>
        {msg}
    </Popover>
);

interface IProps {
    data: IDonorRegistration[],
    isSuperAdmin: boolean,
    centerId: number,
    subSection: string,
    gridHeight: string,
    isExpertSystemParticipant: boolean,
    statusList: IDonorStatus[],
    coordinatorList: ICoordinator[],
    onViewRegistration: (id: number) => void,
    onItemSelected: (id: number, action: DonorIntakeAction) => void,
    onAssignedCoordinatorChange: (regId: number, contactId: number) => void,
}

const Grid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
    }

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onBodyScroll = (e: BodyScrollEvent) => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.setColumnDefs(getColumnChanges());
        e.api.sizeColumnsToFit();
    };

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const getColumnChanges = () => {
        let cols: ColDef[] = [
            {
                colId: '1',
                field: 'rank',
                headerName: 'R#',
                headerComponentFramework: (params: IHeaderParams) => <div>R#</div>,
                sortable: false,
                width: 30,
                pinned: 'left',
                pinnedRowCellRendererFramework: () => <div></div>,
                valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
            },
            {
                field: 'source',
                headerName: 'Source',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Source</SortableHeaderComponent>,
                sortable: true,
                width: 80,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IDonorRegistration;

                    let style = 'default';

                    switch (data.source) {
                        case 'NKR': style = 'info'; break;
                        case 'DASH': style = 'default'; break;
                        case 'MiSite': style = 'success'; break;
                        case 'UDASH': style = 'warning'; break;
                    }

                    return <Label bsStyle={style}>{params.value}</Label>;
                }
            },
            {
                field: 'donationType',
                headerName: 'O',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><span title='Organ'>Org</span></SortableHeaderComponent>,
                sortable: true,
                width: 30,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IDonorRegistration;
                    return data.donationType == 0 ? <Label bsStyle="info" title="Kidney">K</Label> : <Label bsStyle="warning" title="Liver">L</Label>;
                }
            },

            {
                field: 'fullName',
                headerName: 'Name',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Name</SortableHeaderComponent>,
                sortable: true,
                width: 220,
            },
            {
                field: 'currentRegistrationStep',
                headerName: 'currentRegistrationStep',
                hide: props.subSection != 'ALL' || window.innerWidth < 1600,
                sortingOrder: ['desc', 'asc', null],
                sortable: true,
                width: 150,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IDonorRegistration;
                    return data.currentRegistrationStepStatus;
                },
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Registration<br />Status</SortableHeaderComponent>,
            },
            {
                field: '',
                headerName: 'isInExpertSystem',
                sortable: true,
                width: 30,
                hide: props.isExpertSystemParticipant == false,
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><span title="Expert System">In<br />E/S</span></SortableHeaderComponent>,
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as IDonorRegistration;
                    return data.isInExpertSystem ? 'Yes' : 'No';
                },
                cellRendererFramework: (props: ICellRendererParams) => {
                    let data = props.data as IDonorRegistration;

                    if (data.isInExpertSystem) {
                        return (
                            <Glyphicon glyph='ok' />
                        )
                    }

                    return null;
                }
            },
            {
                field: 'type',
                headerName: 'Type',
                sortable: true,
                width: 80,
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type</SortableHeaderComponent>,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IDonorRegistration;

                    let vem = data.voucherEducationChoiceId == VoucherEducationChoice.WantsToLearnMore ? 'i' : '';

                    let style = '';
                    let label = data.type;

                    switch (data.type) {
                        case 'SV': style = 'success'; label = data.type + "" + vem; break;
                        case 'FV': style = 'success'; label = data.type + "" + vem; break;
                        case 'NDD': style = 'info'; break;
                        case 'DD': style = 'default'; break;
                        default: style = 'default'; break;
                    }

                    return <Label bsStyle={style}>{label}</Label>;
                }
            },
            {
                field: 'closeLoopLinkedDonorType',
                headerName: 'Type Donated',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type <br />Donated</SortableHeaderComponent>,
                sortable: true,
                hide: props.subSection != 'ALL' || window.innerWidth < 1600,
                width: 90,
                sortingOrder: ['desc', 'asc', null],
                comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
                    return nodeA.data.closeLoopEntityType == nodeB.data.closeLoopEntityType
                        ? 0
                        : (nodeA.data.closeLoopEntityType > nodeB.data.closeLoopEntityType ? 1 : -1);
                },
                cellRendererFramework: (props: ICellRendererParams) => {
                    let data = props.data as IDonorRegistration;
                    return <ClosedLoopLinkedDonorTypeRenderer {...data} />
                }
            },
            {
                field: 'closeLoopEpletMismatch',
                headerName: 'Eplet MM',
                sortable: true,
                hide: props.subSection != 'ALL' || window.innerWidth < 1600,
                width: 100,
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Eplet<br />MM</SortableHeaderComponent>,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IDonorRegistration;

                    let style = data.closeLoopEpletMismatch == "Med"
                        ? "warning"
                        : (data.closeLoopEpletMismatch == "High" ? "danger" : 'success');
                    let label = data.closeLoopEpletMismatch;

                    if (label == '--') {
                        return <>--</>
                    }

                    return <Label bsStyle={style}>{label}</Label>;
                }
            },
            {
                field: 'labStatus',
                headerName: '',
                sortable: true,
                width: 40,
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Labs</SortableHeaderComponent>,
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as IDonorRegistration;
                    return data.preWorkupLabStep;
                },
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IDonorRegistration;

                    if (data.isRepeat || data.hasFile || data.preWorkupLabStep != null) {
                        let msg = '';
                        let className = '';

                        if (data.isRepeat && data.hasFile) {
                            msg = 'Repeat Labs Complete';
                            className = 'glyphicon glyphicon-star-empty';
                        } else if (data.isRepeat) {
                            msg = 'Repeat Labs In Progress';
                            className = 'glyphicon glyphicon-repeat';
                        } else if (data.hasFile) {
                            msg = 'Labs Complete';
                            className = 'glyphicon glyphicon-star';
                        }
                        else {
                            msg = data.preWorkupLabStatus;
                            className = 'glyphicon glyphicon-time';
                        }

                        return (
                            <ButtonToolbar>
                                <OverlayTrigger
                                    trigger={['hover', 'focus']}
                                    placement='bottom'
                                    overlay={popoverHoverFocus(msg)}>
                                    <span className={className}></span>
                                </OverlayTrigger>
                            </ButtonToolbar>
                        );
                    }

                    return <></>;
                }
            },
            {
                field: '',
                headerName: 'Status',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
                sortable: true,
                sortingOrder: ['desc', 'asc', null],
                width: 100,
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as IDonorRegistration;
                    if (data.currentRegistrationStep < 1000) { return 0; }
                    if (data.currentRegistrationStep == 3001) { return 2; }
                    if (data.currentRegistrationStep == 3000) { return 1; }
                    if (data.currentRegistrationStep >= 2000) { return 3; }
                    if (data.currentRegistrationStep >= 1000 && data.hasUsedAlcohol === null) { return 4; }
                    if (data.currentRegistrationStep >= 1000 && data.hasUsedAlcohol !== null) { return 5; }
                },
                cellRendererFramework: (params: ICellRendererParams) => {
                    var msg, className, color = "black";
                    if (params.data.currentRegistrationStep == 3001) { msg = "Duplicate", className = 'glyphicon glyphicon-duplicate'; }
                    else if (params.data.currentRegistrationStep == 3000) { msg = "Transferred", className = 'glyphicon glyphicon-transfer'; }
                    else if (params.data.currentRegistrationStep >= 2000) { msg = "Ruled Out", className = 'glyphicon glyphicon-remove', color = "#CC0000"; }
                    else if (params.data.currentRegistrationStep < 1000 || params.data.hasUsedAlcohol === null) { msg = "Incomplete", className = 'glyphicon glyphicon-adjust', color = "orange"; }
                    else if (params.data.currentRegistrationStep >= 1000) { msg = "Complete", className = 'glyphicon glyphicon-check', color = "#00AA00"; }

                    return (
                        <ButtonToolbar>
                            <OverlayTrigger
                                trigger={['hover', 'focus']}
                                placement='bottom'
                                overlay={popoverHoverFocus(msg)}>
                                <span className={className} style={{ color: color }}></span>
                            </OverlayTrigger>
                        </ButtonToolbar>
                    );
                }
            },
            {
                field: 'centerWorkflowId',
                headerName: 'Workflow Status',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Workflow <br /> Status</SortableHeaderComponent>,
                width: 200,
                sortable: true,
                valueGetter: (params: ValueGetterParams) => {
                    if (params.data.centerWorkflowId > 0 && props.statusList.length > 0) {
                        let statusItem = props.statusList != null ? props.statusList.find(x => x.id == params.data.centerWorkflowId) : null;

                        return statusItem != null ? statusItem.description : '';
                    }

                    return '--';
                }
            },
            {
                field: 'isInvitedToDonorShield',
                headerName: 'Donor Shield Invited',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Shield<br />Invited</SortableHeaderComponent>,
                width: 140,
                sortable: true,
                hide: false,
                valueGetter: (params: ValueGetterParams) => {
                    const data = params.data as IDonorRegistration;
                    return data.isInvitedToDonorShield ? 'Yes' : 'No';
                },
                cellRendererFramework: (params: ICellRendererParams) => {
                    const data = params.data as IDonorRegistration;
                    return data.isInvitedToDonorShield
                        ? <Label bsStyle='success'>Yes</Label>
                        : <Label bsStyle='default'>No</Label>;
                }
            },
            {
                field: 'countryOfResidenceId',
                headerName: 'Intl Donor',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Intl<br />Donor</SortableHeaderComponent>,
                width: 90,
                sortable: true,
                hide: false,
                valueGetter: (params: ValueGetterParams) => {
                    const data = params.data as IDonorRegistration;
                    return hasForeignResidence(data.countryOfResidenceId) ? 'Yes' : 'No';
                },
                cellRendererFramework: (params: ICellRendererParams) => {
                    const data = params.data as IDonorRegistration;
                    const isIntlDonor = hasForeignResidence(data.countryOfResidenceId);
                    return <Label bsStyle={isIntlDonor ? 'success' : 'default'}>{isIntlDonor ? 'Yes' : 'No'}</Label>;
                }
            },
            {
                field: 'disposition',
                headerName: 'Network Status',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Network Status</SortableHeaderComponent>,
                width: 200,
                sortable: true,
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as IDonorRegistration;

                    if (data.disposition)
                        return data.disposition;

                    return '';
                }
            },
            {
                field: 'registrationDateTime',
                headerName: 'Registered',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Registered<br />Date</SortableHeaderComponent>,
                sortable: true,
                hide: props.subSection != 'ALL',
                width: 150,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IDonorRegistration;

                    return <>{moment(data.registrationDateTime).format("MM/DD/YYYY")}</>
                },
                comparator: Utility.dateComparator
            },
            {
                field: 'historyCompleteDateTime',
                headerName: 'Referred',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Referred<br />Date</SortableHeaderComponent>,
                sortable: true,
                hide: props.subSection != 'ALL' || window.innerWidth < 1600,
                width: 150,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IDonorRegistration;

                    if (data.historyCompleteDateTime == '') {
                        return '';
                    }

                    return <>{data.historyCompleteDateTime}</>
                },
                comparator: Utility.dateComparator
            },
            {
                field: 'closeLoopSurgeryDateTime',
                headerName: 'Donation Date',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donation<br />Date</SortableHeaderComponent>,
                sortable: true,
                hide: props.subSection != 'ALL' || window.innerWidth < 1600,
                width: 130,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IDonorRegistration;

                    if (data.closeLoopSurgeryDateTime == null) {
                        return '--';
                    }

                    return <>{data.closeLoopSurgeryDateTime}</>
                },
                comparator: Utility.dateComparator
            },
            {
                colId: 'centerNextFollowUpDate',
                field: 'centerNextFollowUpDate',
                headerName: 'Next Follow-Up',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Next<br />Follow-Up<br />Date</SortableHeaderComponent>,
                width: 150,
                sortable: true,
                sort: "asc",
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IDonorRegistration;

                    let nextFollowUpDate = moment(data.centerNextFollowUpDate, 'MM/DD/YYYY', true);

                    if (nextFollowUpDate.isBefore(moment(), 'day')) {
                        return <Label bsStyle='danger'>{data.centerNextFollowUpDate}</Label>
                    }

                    if (nextFollowUpDate.isAfter(moment(), 'day')) {
                        return <Label bsStyle='success'>{data.centerNextFollowUpDate}</Label>
                    }

                    if (nextFollowUpDate.isSame(moment(), 'day')) {
                        return <Label bsStyle='warning'>{data.centerNextFollowUpDate}</Label>
                    }

                    return null;
                },
                comparator: Utility.dateComparator
            },

            {
                field: 'recipFullName',
                headerName: 'Intended Recipient',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Intended <br />Recipient</SortableHeaderComponent>,
                width: 200,
                sortable: true,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IDonorRegistration;

                    if (data.recipFullName.length == 0) {
                        if (data.type == 'FV') {
                            return <Label bsStyle='warning' style={{ fontSize: '100%' }}>N/A - FV</Label>;
                        } else if (data.type == 'NDD') {
                            return <Label bsStyle='warning' style={{ fontSize: '100%' }}>N/A - NDD</Label>;
                        } else {
                            return <Label bsStyle='warning' style={{ fontSize: '100%' }}>N/A</Label>
                        }
                    } else {
                        return <ButtonToolbar>{data.recipFullName} {' '}
                            <OverlayTrigger
                                trigger={['hover', 'focus']}
                                placement='bottom'
                                overlay={popoverHoverFocus(`${data.recipientLocation}, DOB: ${data.recipDob}`)}>
                                <span className='glyphicon glyphicon-info-sign' title=''></span>
                            </OverlayTrigger>
                        </ButtonToolbar>
                    }
                }
            },
            {
                colId: 'coordinatorId',
                field: 'coordinatorId',
                headerName: 'Donor Coordinator',
                headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor<br />Coordinator</SortableHeaderComponent>,
                width: 240,
                cellClass: 'donor-connect-drop-down',
                sortable: true,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IDonorRegistration;
                    let coList = props.coordinatorList;

                    return (
                        <>
                            <FormControl
                                componentClass='select'
                                bsSize='sm'
                                value={data.coordinatorId ?? undefined}
                                disabled={data.isReadOnly}
                                onChange={(e: React.FormEvent<FormControl & HTMLSelectElement>) => props.onAssignedCoordinatorChange(data.id, parseInt(e.currentTarget.value))}
                            >
                                <option value={0}>- Not Specified -</option>

                                {coList != null && (
                                    coList.map((item) => {
                                        return <option key={_.uniqueId("co")} value={item.contactId}>{item.fullName}</option>
                                    }
                                    )
                                )}
                            </FormControl>
                        </>
                    )
                }
            },

            {
                colId: 'actionMenu',
                field: 'actionId',
                headerName: 'Actions',
                headerComponentFramework: (params: IHeaderParams) => <div {...params}>Actions</div>,
                width: 110,
                cellClass: 'action-menu',
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IDonorRegistration;
                    let { centerId } = data;

                    return (
                        <ActionsDropdown
                            {...params}
                            // centerId={centerId}
                            donor={data}
                            showLinkToDetails={true}
                            showBsMenu={false}
                            isSuperAdmin={props.isSuperAdmin}
                            onMenuToggle={handleMenuToggle}
                            onItemSelected={props.onItemSelected}
                        />
                    );
                }
            }
        ]
        return cols;
    };

    const getHeight = () => {
        let height = '60vh';

        if (props.subSection == 'ALL') {
            height = '45vh';
        }

        if (props.centerId == 0) {
            height = '65vh'
        }

        return height;
    };

    const columnDefs = useMemo(() => getColumnChanges(), [props.coordinatorList]);
    const rowData = useMemo(() => props.data, [props.data]);
    const height = getHeight();

    return (
        <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' style={{ height: props.gridHeight }}>
            <AgGridReact
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={props.isSuperAdmin ? 60 : 55}
                rowHeight={34}
                enableCellChangeFlash={true}
                suppressCellSelection={true}
                suppressMultiSort={true}
                rowStyle={{ overflow: 'visible' }}
                getRowClass={(params) => params.data?.isReadOnly ? 'domain-view-only-row' : ''}
                noRowsOverlayComponentFramework={() => <div>...</div>}
                onGridReady={onGridReady}
                onGridSizeChanged={onGridSizeChanged}
                onFirstDataRendered={onFirstDataRendered}
                onRowDataChanged={onRowDataChanged}
                onBodyScroll={onBodyScroll}
                onCellClicked={(e: CellClickedEvent) => {
                    if (e.colDef.field != 'coordinatorId' && e.colDef.field != 'actionId') {
                        let { id } = (e.data as IDonorRegistration);
                        props.onViewRegistration(id);
                    }
                }}
                onSortChanged={(e: SortChangedEvent) => e.api.refreshCells()}
            />
        </GridWrapper>
    )
}

export default Grid;